import React, { useRef, useState } from 'react';
import { 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions, 
    TextField, 
    Button, 
    Box,
    MenuItem, 
    CircularProgress 
} from '@mui/material';
import { useLanguage } from '../../../../../context/LanguageContext';
import { Store } from '../../../../../types/Store';
import useUpdateStore from '../../../../../hooks/Magasins/useUpdateStore';
import { useOptimisticStore } from '../../../../../context/OptimisticStoreContext';
import { toast } from 'react-toastify';

interface EditStoreInfoDrawerProps {
    open: boolean;
    onClose: () => void;
    store: Store;
    onSuccess?: () => void;
}

const EditStoreInfoDrawer = ({ open, onClose, store, onSuccess }: EditStoreInfoDrawerProps) => {
    const { t } = useLanguage();
    const { updateStore, isLoading } = useUpdateStore();
    const { actualStore, setActualStore } = useOptimisticStore();

    const identifierRef = useRef<HTMLInputElement | null>(null);
    const nameRef = useRef<HTMLInputElement | null>(null);
    const entityNumberRef = useRef<HTMLInputElement | null>(null);
    const entityTypeRef = useRef<HTMLInputElement | null>(null);
    const franchiseRef = useRef<HTMLInputElement | null>(null);
    const transactionModeRef = useRef<HTMLInputElement | null>(null);
    const paymentOverdueRef = useRef<HTMLInputElement | null>(null);

    const [errors, setErrors] = useState<Record<string, string>>({});
    const currentEntityType = (store.ENTITY_TYPE || 'STORE').toString().toUpperCase();
    const canEditEntityType = currentEntityType === 'STORE' || currentEntityType === 'STATISTIC';

    const validateField = (field: string, value: string): string => {
        const maxLengths: Record<string, number> = {
            entity_number: 6,
            identifier: 10,
            name: 50,
            entity_type: 20,
            franchise: 1,
            transaction_integration_mode: 10,
            payment_overdue: 12
        };

        if (field === 'identifier' && !value.trim()) {
            return t('pages.store.errors.required');
        }

        if (field === 'name' && !value.trim()) {
            return t('pages.store.errors.required');
        }

        if (maxLengths[field] && value.length > maxLengths[field]) {
            return t('pages.store.errors.maxLength', { max: maxLengths[field] }) || 
                   `La longueur maximale est de ${maxLengths[field]} caractères`;
        }

        return '';
    };

    const handleBlur = (field: string, ref: React.RefObject<HTMLInputElement | null>) => {
        const value = ref.current?.value || '';
        const error = validateField(field, value);
        setErrors(prev => ({ ...prev, [field]: error }));
    };

    const getFormData = () => {
        const formData: Record<string, string> = {
            entity_number: entityNumberRef.current?.value || '',
            identifier: identifierRef.current?.value || '',
            name: nameRef.current?.value || '',
            franchise: franchiseRef.current?.value || '0',
            transaction_integration_mode: transactionModeRef.current?.value || '0',
            payment_overdue: paymentOverdueRef.current?.value || '0',
        };

        if (canEditEntityType) {
            formData.entity_type = entityTypeRef.current?.value || currentEntityType;
        }

        return formData;
    };

    const handleSave = async () => {
        const formData = getFormData();
        
        // Validation complète
        const newErrors: Record<string, string> = {};
        Object.keys(formData).forEach(key => {
            const error = validateField(key, formData[key as keyof typeof formData]);
            if (error) newErrors[key] = error;
        });

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        // Ne envoyer que les champs modifiés
        const changedFields: Record<string, any> = {};
        Object.keys(formData).forEach(key => {
            const fieldKey = key as keyof typeof formData;
            const storeKey = key.toUpperCase() as keyof Store;
            if (formData[fieldKey] !== (store[storeKey] || '')) {
                changedFields[key] = formData[fieldKey];
            }
        });

        if (Object.keys(changedFields).length === 0) {
            onClose();
            return;
        }

        const optimisticUpdates: Partial<Store> = {};
        Object.keys(changedFields).forEach(key => {
            const storeKey = key.toUpperCase() as keyof Store;
            optimisticUpdates[storeKey] = changedFields[key];
        });
        
        if (actualStore) {
            setActualStore({ ...actualStore, ...optimisticUpdates } as Store);
        }
        
        onClose();
        
        const result = await updateStore(store.ENTITY_ID.toString(), changedFields);
        
        if (result) {
            toast.success(t('pages.store.success.updated'));
            onSuccess?.();
        } else {
            if (actualStore) {
                setActualStore(actualStore);
            }
            toast.error(t('pages.store.errors.updateFailed'));
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>
                {t('pages.store.editStoreInfo')}
            </DialogTitle>
            <DialogContent sx={{ paddingTop: '20px!important' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                        fullWidth
                        required
                        label={t('pages.store.name')}
                        inputRef={nameRef}
                        defaultValue={store.NAME || ''}
                        onBlur={() => handleBlur('name', nameRef)}
                        error={!!errors.name}
                        helperText={errors.name}
                        inputProps={{ maxLength: 50 }}
                    />
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                        <TextField
                            fullWidth
                            required
                            label={t('pages.store.storeNumber')}
                            inputRef={identifierRef}
                            defaultValue={store.IDENTIFIER || ''}
                            onBlur={() => handleBlur('identifier', identifierRef)}
                            error={!!errors.identifier}
                            helperText={errors.identifier}
                            inputProps={{ maxLength: 10 }}
                        />
                        <TextField
                            fullWidth
                            label={t('pages.store.internalCode')}
                            inputRef={entityNumberRef}
                            defaultValue={store.ENTITY_NUMBER || ''}
                            onBlur={() => handleBlur('entity_number', entityNumberRef)}
                            error={!!errors.entity_number}
                            helperText={errors.entity_number}
                            inputProps={{ maxLength: 6 }}
                        />
                    </Box>
                    
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: canEditEntityType ? '1fr 1fr 1fr 1fr' : '1fr 1fr 1fr' }, gap: 2 }}>
                        {canEditEntityType && (
                            <TextField
                                select
                                fullWidth
                                label={t('pages.store.type')}
                                inputRef={entityTypeRef}
                                defaultValue={store.ENTITY_TYPE || 'STORE'}
                                onBlur={() => handleBlur('entity_type', entityTypeRef)}
                            >
                                <MenuItem value="STORE">STORE</MenuItem>
                                <MenuItem value="STATISTIC">STATISTIC</MenuItem>
                            </TextField>
                        )}
                        <TextField
                            select
                            fullWidth
                            label={t('pages.store.franchise')}
                            inputRef={franchiseRef}
                            defaultValue={store.FRANCHISE || '0'}
                            onBlur={() => handleBlur('franchise', franchiseRef)}
                        >
                            <MenuItem value="1">FRANCHISE</MenuItem>
                            <MenuItem value="0">INTEGRE</MenuItem>
                        </TextField>
                        <TextField
                            select
                            fullWidth
                            label={t('pages.store.transactionMode')}
                            inputRef={transactionModeRef}
                            defaultValue={store.TRANSACTION_INTEGRATION_MODE || '0'}
                            onBlur={() => handleBlur('transaction_integration_mode', transactionModeRef)}
                        >
                            <MenuItem value="WS">WS</MenuItem>
                            <MenuItem value="1">{t('common.yes')}</MenuItem>
                            <MenuItem value="0">{t('common.no')}</MenuItem>
                        </TextField>
                        <TextField
                            select
                            fullWidth
                            label={t('pages.store.advancePayment')}
                            inputRef={paymentOverdueRef}
                            defaultValue={store.PAYMENT_OVERDUE || '0'}
                            onBlur={() => handleBlur('payment_overdue', paymentOverdueRef)}
                        >
                            <MenuItem value="1">{t('common.yes')}</MenuItem>
                            <MenuItem value="0">{t('common.no')}</MenuItem>
                        </TextField>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isLoading} variant="outlined">
                    {t('common.cancel')}
                </Button>
                <Button 
                    variant="contained" 
                    onClick={handleSave} 
                    disabled={isLoading}
                    startIcon={isLoading ? <CircularProgress size={20} /> : null}
                >
                    {isLoading ? t('common.saving') : t('common.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditStoreInfoDrawer;
